import { useState, useEffect, useCallback, useRef } from 'react';
import { scenarios as scenariosApi } from '../../../services/api';
import { Scenario, ScenarioSummary, CreateScenarioDto, UpdateScenarioDto, ScenarioPaginationParams } from '../types';

export function useScenarios(initialFilters?: ScenarioPaginationParams) {
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [loading, setLoading] = useState(initialFilters?.autoFetch !== false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(initialFilters?.page || 1);
  const [limit, setLimit] = useState<number>(initialFilters?.limit || 20);
  const [search, setSearch] = useState<string>(initialFilters?.search || '');
  const [status, setStatus] = useState<string>(initialFilters?.status || 'ALL');
  const [category, setCategory] = useState<string>(initialFilters?.category || 'ALL');
  const [difficulty, setDifficulty] = useState<string>(initialFilters?.difficulty || 'ALL');
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const filterRef = useRef<ScenarioPaginationParams | undefined>(initialFilters);
  filterRef.current = initialFilters;

  const shouldAutoFetch = initialFilters?.autoFetch !== false;

  const requestSeqRef = useRef<number>(0);

  const fetchScenarios = useCallback(
    async (overrideFilters?: Partial<ScenarioPaginationParams>) => {
      const currentSeq = ++requestSeqRef.current;
      setLoading(true);
      setError(null);
      try {
        const merged = {
          ...filterRef.current,
          page: overrideFilters?.page !== undefined ? overrideFilters.page : page,
          limit: overrideFilters?.limit !== undefined ? overrideFilters.limit : limit,
          search: overrideFilters?.search !== undefined ? overrideFilters.search : search,
          status: (overrideFilters?.status !== undefined ? overrideFilters.status : status) !== 'ALL' ? (overrideFilters?.status !== undefined ? overrideFilters.status : status) : undefined,
          category: (overrideFilters?.category !== undefined ? overrideFilters.category : category) !== 'ALL' ? (overrideFilters?.category !== undefined ? overrideFilters.category : category) : undefined,
          difficulty: (overrideFilters?.difficulty !== undefined ? overrideFilters.difficulty : difficulty) !== 'ALL' ? (overrideFilters?.difficulty !== undefined ? overrideFilters.difficulty : difficulty) : undefined,
          ...overrideFilters,
        };
        const { autoFetch, ...apiFilters } = merged;
        const res = await scenariosApi.list(apiFilters);
        if (currentSeq !== requestSeqRef.current) {
          // Discard stale out-of-order response
          return;
        }
        if (res.success && res.data) {
          const list = res.data.scenarios || [];
          setScenarios(list);
          const totalCount = res.data.total ?? list.length;
          setTotal(totalCount);
          const activeLimit = merged.limit || limit || 10;
          const calculatedTotalPages = res.data.totalPages ?? Math.max(1, Math.ceil(totalCount / activeLimit));
          setTotalPages(calculatedTotalPages);
          setHasMore(res.data.hasMore ?? (merged.page || page) < calculatedTotalPages);
        } else {
          setError(res.error?.message || 'Failed to load scenarios');
        }
      } catch (err: any) {
        if (currentSeq === requestSeqRef.current) {
          setError(err?.message || 'An unexpected error occurred while fetching scenarios.');
        }
      } finally {
        if (currentSeq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [page, limit, search, status, category, difficulty]
  );

  useEffect(() => {
    if (!shouldAutoFetch) return;
    const timer = setTimeout(() => {
      fetchScenarios();
    }, 150);
    return () => clearTimeout(timer);
  }, [shouldAutoFetch, page, limit, search, status, category, difficulty]);

  const getScenario = async (id: string): Promise<Scenario | null> => {
    try {
      const res = await scenariosApi.get(id);
      if (res.success && res.data) {
        return res.data;
      }
      return null;
    } catch {
      return null;
    }
  };

  const createScenario = async (data: CreateScenarioDto): Promise<{ success: boolean; data?: Scenario; error?: string }> => {
    try {
      const res = await scenariosApi.create(data);
      if (res.success && res.data) {
        if (shouldAutoFetch) {
          fetchScenarios().catch(() => {});
        }
        return { success: true, data: res.data };
      }
      return { success: false, error: res.error?.message || 'Failed to create scenario' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to create scenario' };
    }
  };

  const updateScenario = async (
    id: string,
    data: UpdateScenarioDto
  ): Promise<{ success: boolean; data?: Scenario; error?: string }> => {
    try {
      const res = await scenariosApi.update(id, data);
      if (res.success && res.data) {
        if (shouldAutoFetch) {
          fetchScenarios().catch(() => {});
        }
        return { success: true, data: res.data };
      }
      return { success: false, error: res.error?.message || 'Failed to update scenario' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to update scenario' };
    }
  };

  const publishScenario = async (id: string): Promise<{ success: boolean; data?: Scenario; error?: string }> => {
    try {
      const res = await scenariosApi.publish(id);
      if (res.success && res.data) {
        if (shouldAutoFetch) {
          fetchScenarios().catch(() => {});
        }
        return { success: true, data: res.data };
      }
      return { success: false, error: res.error?.message || 'Failed to publish scenario' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to publish scenario' };
    }
  };

  const unpublishScenario = async (id: string): Promise<{ success: boolean; data?: Scenario; error?: string }> => {
    try {
      const res = await scenariosApi.unpublish(id);
      if (res.success && res.data) {
        if (shouldAutoFetch) {
          fetchScenarios().catch(() => {});
        }
        return { success: true, data: res.data };
      }
      return { success: false, error: res.error?.message || 'Failed to unpublish scenario' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to unpublish scenario' };
    }
  };

  const deleteScenario = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await scenariosApi.delete(id);
      if (res.success) {
        if (shouldAutoFetch) {
          fetchScenarios().catch(() => {});
        }
        return { success: true };
      }
      return { success: false, error: res.error?.message || 'Failed to delete scenario' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to delete scenario' };
    }
  };

  const duplicateScenario = async (id: string): Promise<{ success: boolean; data?: Scenario; error?: string }> => {
    try {
      const res = await scenariosApi.duplicate(id);
      if (res.success && res.data) {
        if (shouldAutoFetch) {
          fetchScenarios().catch(() => {});
        }
        return { success: true, data: res.data };
      }
      return { success: false, error: res.error?.message || 'Failed to duplicate scenario' };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to duplicate scenario' };
    }
  };

  return {
    scenarios,
    loading,
    error,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    status,
    setStatus,
    category,
    setCategory,
    difficulty,
    setDifficulty,
    total,
    totalPages,
    hasMore,
    fetchScenarios,
    getScenario,
    createScenario,
    updateScenario,
    publishScenario,
    unpublishScenario,
    duplicateScenario,
    deleteScenario,
  };
}
